import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AWAITING_CLASS_VALUE, studentClassStatus } from '../../utils/classStatus';
import { authApi, studentApi } from '../../services/api';
import MedalCelebration from '../../components/MedalCelebration';
import ProfileEditView from '../../components/ProfileEditView';
import AvatarUpload from '../../components/AvatarUpload';

// ---- Medal data ----
export interface Medal {
  id: string;
  title: string;
  description?: string;
  tier: 'standard' | 'bronze' | 'silver' | 'gold' | 'master';
  cardImage: string;
  owned: boolean;
  progress?: number;
  criteriaValue?: number;
  /** ISO tarih — cascade ile kazanılan (üst tier'dan gelen) madalyalarda null olabilir. */
  earnedAt?: string | null;
}

// No static medal catalog — the entire list (definitions + per-student
// ownership + showcase flags) comes from /student/medals. The card PNGs
// shipped in /public/medals/cards/ keep the visual identity; the DB drives
// what to show and in what state.

// ---- Medal Card (matches Figma node 107:2206) ----
// The PNGs in /public/medals/cards/ are an early Figma export with
// placeholder "Madalya Açıklaması / Madalya Açıklaması" + a fake "1/1"
// progress baked into the bottom 38% of every bitmap. The actual Figma
// component (file 6nrQCBxlgi5ODYgeroTZk8 → Madalya frame) is a clean
// composition: tier-coloured card art on top, then quest description +
// progress in a tier-toned panel.
//
// We mask that bottom 38% with a panel coloured to match the tier and
// render description + progress fresh from /student/medals on top. The
// upper 62% of the original PNG (medal coin + character + title) stays
// untouched so the gold gradient, bronze brown, master face decoration,
// etc. all show through as designed.
// Figma medal layout (123x190 baseline):
//   0-6%   : padding
//   6-43%  : medal coin
//   46-62% : title text  ← baked
//   62-84% : EMPTY ← description overlay HERE
//   84-88% : "1/1" text  ← baked (placeholder, must be covered)
//   91-93% : progress bar Rectangle 33  ← baked (placeholder, must be covered)
//
// We add two overlays:
//   1. Description text in the 62-84% empty band.
//   2. A tier-coloured strip from 80% downward that COVERS the baked "1/1"
//      + progress bar with the real DB values (current progress / criteria).
// Tier colours sampled from Figma so the cover blends with the card art.
const TIER_COVER: Record<Medal['tier'], { bg: string; text: string; track: string; fill: string }> = {
  standard: { bg: '#fafafa', text: '#2b2b2b', track: '#1e1e1e', fill: '#c7b294' },
  bronze: { bg: '#867a64', text: '#fafafa', track: '#1e1e1e', fill: '#fafafa' },
  silver: { bg: '#d2d0d1', text: '#2b2b2b', track: '#737373', fill: '#fafafa' },
  gold: { bg: '#e5b93f', text: '#2b2b2b', track: '#3a2b0a', fill: '#fafafa' },
  master: { bg: '#3d3d3d', text: '#fafafa', track: '#1f1f1f', fill: '#fafafa' },
};

export const MedalDescriptionOverlay: React.FC<{
  description?: string;
  tier?: Medal['tier'];
  progress?: number;
  criteriaValue?: number | null;
  large?: boolean;
}> = ({ description, tier = 'standard', progress, criteriaValue, large }) => {
  const ts = TIER_COVER[tier] ?? TIER_COVER.standard;
  const total = criteriaValue ?? 0;
  const done = progress ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (done > 0 ? 100 : 0);
  return (
    <>
      {/* Description in the empty band (#7 — yarı şeffaf koyu zemin + biraz
          daha büyük punto). Tam metni okumak için karta tıklayınca açılan
          modal kullanılıyor (MedalCard onSelect → setMedalDetail). */}
      {description && (
        <div
          className="absolute inset-x-0 flex items-center justify-center pointer-events-none"
          style={{
            top: '60%',
            bottom: '20%',
            padding: large ? '0 10px' : '0 6px',
            textAlign: 'center',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.55) 100%)',
          }}
        >
          <p
            style={{
              fontFamily: 'Inter',
              fontSize: large ? 12 : 9,
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.9)',
              margin: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: large ? 3 : 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </p>
        </div>
      )}

      {/* Cover strip — solid tier colour over the baked "1/1" + progress
          bar so the real DB-driven values can be rendered in their place. */}
      <div
        className="absolute inset-x-0 flex flex-col justify-center pointer-events-none"
        style={{
          bottom: 0,
          height: '20%',
          background: ts.bg,
          padding: large ? '0 16px' : '0 10px',
        }}
      >
        <p
          style={{
            fontFamily: 'Inter',
            fontSize: large ? 10 : 8,
            fontWeight: 600,
            color: ts.text,
            textAlign: 'center',
            lineHeight: 1,
            margin: '0 0 4px',
          }}
        >
          {total > 0 ? `${done}/${total}` : (done > 0 ? '✓' : '')}
        </p>
        <div
          style={{
            height: large ? 4 : 3,
            background: ts.track,
            borderRadius: large ? 2 : 1.5,
            overflow: 'hidden',
          }}
        >
          <div style={{ height: '100%', width: `${pct}%`, background: ts.fill, transition: 'width 0.4s ease' }} />
        </div>
      </div>
    </>
  );
};

const MedalCard: React.FC<{
  medal: Medal;
  size?: 'small' | 'large';
  showStar?: boolean;
  isShowcase?: boolean;
  onToggleShowcase?: (id: string) => void;
  onSelect?: (medal: Medal) => void;
}> = ({ medal, size = 'small', showStar = false, isShowcase = false, onToggleShowcase, onSelect }) => {
  const isLarge = size === 'large';
  const w = isLarge ? 162 : 123;
  const h = isLarge ? 250 : 190;

  const tooltip = medal.description ? `${medal.title}\n${medal.description}` : medal.title;
  return (
    <div
      className="relative flex-shrink-0 cursor-pointer transition-transform duration-200 hover:scale-[1.06] hover:z-20"
      style={{ width: w, height: h, borderRadius: 12, overflow: 'hidden' }}
      title={tooltip}
      onClick={() => onSelect?.(medal)}
    >
      {showStar && medal.owned && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleShowcase?.(medal.id); }}
          className="absolute top-2 right-2 w-[25px] h-[25px] rounded-full bg-white flex items-center justify-center z-10"
          style={{ boxShadow: '0px 4px 4px rgba(0,0,0,0.25)' }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill={isShowcase ? '#e5599b' : 'none'} stroke="#e5599b" strokeWidth="1.5">
            <path d="M6 1l1.5 3.1L11 4.5 8.5 7l.6 3.5L6 8.8 2.9 10.5l.6-3.5L1 4.5l3.5-.4z" />
          </svg>
        </button>
      )}
      {!medal.owned && (
        <div
          className="absolute top-2 left-2 rounded-full bg-white/90 flex items-center justify-center z-10"
          style={{ width: isLarge ? 28 : 24, height: isLarge ? 28 : 24, boxShadow: '0px 2px 4px rgba(0,0,0,0.25)' }}
          title="Henüz kazanılmadı"
        >
          <svg width={isLarge ? 14 : 12} height={isLarge ? 14 : 12} viewBox="0 0 24 24" fill="none" stroke="#6b6b6b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="11" width="16" height="9" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" />
          </svg>
        </div>
      )}
      <img
        src={medal.cardImage}
        alt={medal.title}
        className="w-full h-full object-cover"
        style={medal.owned ? undefined : { filter: 'grayscale(1) brightness(0.9)', opacity: 0.55 }}
      />
      <MedalDescriptionOverlay
        description={medal.description}
        tier={medal.tier}
        progress={medal.progress}
        criteriaValue={medal.criteriaValue}
        large={isLarge}
      />
    </div>
  );
};

type View = 'profile' | 'medals' | 'edit-password';

const StudentProfile: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('profile');
  const studentId = user?.studentCode ?? '-';
  const [copyToast, setCopyToast] = useState(false);
  const [celebration, setCelebration] = useState<{
    title: string;
    image: string;
    description?: string;
    tier: Medal['tier'];
    progress?: number;
    criteriaValue?: number;
  } | null>(null);
  const [allMedals, setAllMedals] = useState<Medal[]>([]);
  const [medalsLoading, setMedalsLoading] = useState<boolean>(true);
  const [showcaseIds, setShowcaseIds] = useState<string[]>([]);
  const [medalDetail, setMedalDetail] = useState<Medal | null>(null);
  // Server gives each earned achievement a numeric id we need for PATCH; map
  // by the `name` ("gold-beyin-tam-kapasite") so the client can stay declarative.
  const [nameToServerId, setNameToServerId] = useState<Record<string, string>>({});

  useEffect(() => {
    studentApi.medals()
      .then((res) => {
        const rows = (res.data?.all ?? []) as Array<{
          id: string; name: string; title: string; tier: Medal['tier'];
          cardImage: string; owned: boolean; progress?: number;
          isShowcased?: boolean; criteriaValue?: number;
        }>;
        if (!Array.isArray(rows)) return;
        const mapped: Medal[] = rows.map((r: any) => ({
          id: r.name,
          title: r.title,
          description: r.description,
          tier: r.tier,
          cardImage: r.cardImage,
          owned: r.owned,
          progress: r.progress,
          criteriaValue: r.criteriaValue,
          earnedAt: r.earnedAt ?? null,
        }));
        setAllMedals(mapped);
        setShowcaseIds(rows.filter((r) => r.isShowcased).map((r) => r.name));
        setNameToServerId(Object.fromEntries(rows.map((r) => [r.name, r.id])));
      })
      .catch((e) => { console.error('medals load failed', e); })
      .finally(() => setMedalsLoading(false));
  }, []);

  const toggleShowcase = useCallback((id: string) => {
    const serverId = nameToServerId[id];
    const willShow = !showcaseIds.includes(id);
    // Optimistic update; revert on failure
    setShowcaseIds((prev) => willShow ? [...prev, id] : prev.filter((x) => x !== id));
    if (!serverId) return;
    studentApi.setMedalShowcase(serverId, willShow).catch((e) => {
      console.error('showcase toggle failed', e);
      setShowcaseIds((prev) => willShow ? prev.filter((x) => x !== id) : [...prev, id]);
    });
  }, [nameToServerId, showcaseIds]);

  const handleCopyId = () => {
    navigator.clipboard.writeText(studentId).catch(() => { });
    setCopyToast(true);
  };

  useEffect(() => {
    if (!copyToast) return;
    const t = setTimeout(() => setCopyToast(false), 2000);
    return () => clearTimeout(t);
  }, [copyToast]);

  // Call this when a medal is earned (from API response, etc.)
  const celebrateMedal = useCallback((medal: Medal) => {
    setCelebration({
      title: medal.title,
      image: medal.cardImage,
      description: medal.description,
      tier: medal.tier,
      progress: medal.progress,
      criteriaValue: medal.criteriaValue,
    });
  }, []);

  // To trigger celebration from API: celebrateMedal(medal)
  // Example: celebrateMedal(allMedals.find(m => m.id === 'gold-beyin-tam-kapasite')!)

  // Showcase medals ordered: master > gold > silver > bronze > standard
  const tierOrder: Medal['tier'][] = ['master', 'gold', 'silver', 'bronze', 'standard'];
  const showcaseMedals = tierOrder.flatMap(tier =>
    allMedals.filter(m => m.tier === tier && showcaseIds.includes(m.id))
  );

  const [fresh, setFresh] = useState<any>(null);

  // Always fetch /me on mount — localStorage might hold a pre-refactor
  // user shape missing phone/institution/grade, so the profile shows dashes
  // until we refresh from the server.
  useEffect(() => {
    authApi.me()
      .then((res) => setFresh(res.data))
      .catch(() => { /* leave fresh=null; fall back to context */ });
  }, []);

  const u: any = fresh ?? user;
  // /auth/me sends name = first_name and surname = last_name; the context's user.name
  // is "first last" (AuthContext). Dropping the last word of whichever arrived cut
  // two-word first names ("Ayşe Nur" → "Ayşe"). Take the fresh first name as is, and
  // strip the surname off the context's full name only (teacher Profile's rule).
  const ad = fresh
    ? (fresh.name || '-')
    : u?.name
      ? (u.surname && String(u.name).endsWith(u.surname) ? String(u.name).slice(0, -String(u.surname).length).trim() : '') || u.name
      : '-';
  const soyad = u?.surname || '-';
  // Grade names come from the DB in English for legacy rows ("Grade 9") —
  // translate on the client so the profile reads as Turkish.
  const translateGrade = (name?: string | null) => {
    if (!name) return '-';
    const m = name.match(/^Grade\s+(\d+)/i);
    return m ? `${m[1]}. Sınıf` : name;
  };
  // Kurumlu but in no class: "Atama bekleniyor" says the kurum still has to place
  // them — "—" read as missing data. Used by both the profile and the edit view.
  const sinifValue = u?.className
    ?? (studentClassStatus(user) === 'awaiting' ? AWAITING_CLASS_VALUE : translateGrade(u?.grade?.name));
  const profileFields = [
    { label: 'Ad', value: ad },
    { label: 'Soyad', value: soyad },
    { label: 'Telefon Numarası', value: u?.phone ?? '-' },
    { label: 'E-posta', value: u?.email ?? '-' },
    { label: 'Okul', value: u?.institution?.name ?? u?.institutions?.[0]?.name ?? '-' },
    { label: 'Sınıf', value: sinifValue },
    { label: 'Günlük Seri', value: u?.currentStreak != null ? `${u.currentStreak} gün` : '-' },
    { label: 'En Uzun Seri', value: u?.longestStreak != null ? `${u.longestStreak} gün` : '-' },
  ];

  // ---- MEDALS VIEW ----
  if (view === 'medals') {
    const byTier = (tier: Medal['tier']) => allMedals.filter(m => m.tier === tier);
    // Vitrin ordered: master > gold > silver > bronze > standard
    const vitrinMedals = tierOrder.flatMap(tier =>
      allMedals.filter(m => m.tier === tier && showcaseIds.includes(m.id))
    );
    const sections: { title: string; items: Medal[]; showStar: boolean }[] = [
      { title: 'Vitrin', items: vitrinMedals, showStar: true },
      { title: 'Sahip Olunan Madalyalar', items: tierOrder.flatMap(tier => allMedals.filter(m => m.tier === tier && m.owned)), showStar: true },
      { title: 'Standart Madalyalar', items: byTier('standard'), showStar: false },
      { title: 'Bronz Madalyalar', items: byTier('bronze'), showStar: false },
      { title: 'Gümüş Madalyalar', items: byTier('silver'), showStar: false },
      { title: 'Altın Madalyalar', items: byTier('gold'), showStar: false },
      { title: 'Ustalık Madalyaları', items: byTier('master'), showStar: false },
    ];

    return (
      <div>
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setView('profile')}
            className="w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round"><path d="M13 4l-6 6 6 6" /></svg>
          </button>
          <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Tüm Madalyalar</h1>
        </div>
        <div className="space-y-5">
          {sections.map(section => (
            <div key={section.title} className="bg-white rounded-[20px] p-5">
              <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#2b2b2b', marginBottom: 12 }}>{section.title}</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {section.items.length > 0 ? section.items.map(m => (
                  <MedalCard key={m.id} medal={m} size="small" showStar={section.showStar}
                    isShowcase={showcaseIds.includes(m.id)} onToggleShowcase={toggleShowcase}
                    onSelect={setMedalDetail} />
                )) : (
                  <p className="text-gray-400 text-[13px] py-4">Henüz madalya yok</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <MedalDetailModal medal={medalDetail} onClose={() => setMedalDetail(null)} />
      </div>
    );
  }

  // ---- EDIT VIEW (standart düzen — ProfileEditView, öğretmenle ortak) ----
  if (view === 'edit-password') {
    return (
      <ProfileEditView
        onBack={() => setView('profile')}
        source={u}
        readOnlyRows={[
          { label: 'Okul', value: u?.institution?.name ?? u?.institutions?.[0]?.name ?? '-' },
          { label: 'Sınıf', value: sinifValue },
          { label: 'Kitap Kodu (değiştirilemez)', value: u?.studentCode ?? '-' },
        ]}
        onSaved={(data) => setFresh(data)}
      />
    );
  }

  // ---- MAIN PROFILE VIEW ----
  return (
    <div>
      {/* Copy toast */}
      {copyToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] shadow-lg animate-pulse">
          ID Kopyalandı!
        </div>
      )}

      {/* Header */}
      <h1 className="mb-5" style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Profil</h1>

      <div className="max-w-[1100px]">
        {/* Hero card — full width: avatar + identity, action top-right */}
        <div className="bg-white rounded-[24px] p-6 mb-5 flex flex-col sm:flex-row sm:items-center gap-5"
          style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          {/* AP35: avatar artık tıklanıp değiştirilebilir */}
          <AvatarUpload size={112} />
          <div className="flex-1 min-w-0">
            <p style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>
              {user?.name || 'Öğrenci'}
            </p>
            <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#6b6b6b', marginBottom: 10 }}>
              {u?.institution?.name ?? u?.institutions?.[0]?.name ?? '-'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full px-3 py-1" style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: '#3973bf', background: 'rgba(57,115,191,0.10)' }}>
                {u?.className ?? translateGrade(u?.grade?.name)}
              </span>
              <button onClick={handleCopyId} title="Öğrenci ID Kopyala"
                className="rounded-full px-3 py-1 flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: '#e5599b', background: 'rgba(229,89,155,0.10)' }}>
                <span>Öğrenci ID: {studentId}</span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              </button>
            </div>
          </div>
          <button onClick={() => setView('edit-password')}
            className="rounded-[16px] px-5 py-2.5 hover:opacity-90 transition-opacity self-start sm:self-center flex-shrink-0"
            style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <span style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Profili Düzenle</span>
          </button>
        </div>

        {/* Madalyalar — full-width section */}
        <div className="flex items-center justify-start gap-4 mb-3 pr-5">
          <h2 style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#2b2b2b' }}>Madalyalar</h2>
          <button onClick={() => setView('medals')}
            className="rounded-[18px] px-5 py-2 text-center whitespace-nowrap hover:opacity-90 transition-opacity"
            style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <span style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#fafafa' }}>Tüm Madalyaları Görüntüle</span>
          </button>
        </div>
        <div className="bg-white rounded-[24px] p-5 mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {medalsLoading ? (
              <div className="flex justify-center w-full py-10">
                <div className="w-7 h-7 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : showcaseMedals.length > 0 ? (
              showcaseMedals.map(m => <MedalCard key={m.id} medal={m} size="large" onSelect={setMedalDetail} />)
            ) : (
              <p className="text-gray-400 text-[14px] py-8 text-center w-full">
                Henüz vitrin madalyası seçilmedi. "Tüm Madalyalar"dan favori madalyalarınızı seçin.
              </p>
            )}
          </div>
        </div>

        {/* Bilgilerim — full-width section below (standart düzen, öğretmen profiliyle aynı) */}
        <div className="bg-white rounded-[24px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <h2 className="mb-5" style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Bilgilerim</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-5">
            {profileFields.map((f, i) => (
              <div key={i} className="min-w-0 border-b border-[#eeeeee] pb-3">
                <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 500, color: '#3973bf', marginBottom: 4 }}>{f.label}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }} className="break-words">{f.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Medal celebration popup */}
      <MedalCelebration
        open={celebration !== null}
        medalTitle={celebration?.title || ''}
        medalImage={celebration?.image || ''}
        medalDescription={celebration?.description}
        medalTier={celebration?.tier}
        medalProgress={celebration?.progress}
        medalCriteriaValue={celebration?.criteriaValue}
        onClose={() => setCelebration(null)}
        onGoToMedals={() => { setCelebration(null); setView('medals'); }}
      />

      <MedalDetailModal medal={medalDetail} onClose={() => setMedalDetail(null)} />
    </div>
  );
};

/**
 * Madalya detay popup'ı — büyük madalya görseli + üstünde açıklama overlay'i
 * + altta minimal durum metni. `createPortal` ile document.body'ye mount
 * edildiği için hangi view'dan açılırsa açılsın ekran ortasında belirir.
 * MedalCelebration ile görsel olarak uyumlu (260×400 görsel, gradient overlay).
 */
const MedalDetailModal: React.FC<{ medal: Medal | null; onClose: () => void }> = ({ medal, onClose }) => {
  useEffect(() => {
    if (!medal) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [medal, onClose]);

  if (!medal) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-[24px] w-full max-w-[480px] p-8 text-center"
        style={{ boxShadow: '0px 8px 32px rgba(0,0,0,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kapat */}
        <button
          onClick={onClose}
          aria-label="Kapat"
          className="absolute top-4 right-4 w-[36px] h-[36px] rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="3" x2="13" y2="13" />
            <line x1="13" y1="3" x2="3" y2="13" />
          </svg>
        </button>

        {/* Madalya görseli — MedalDescriptionOverlay hem açıklamayı gösterir hem
            de PNG'ye gömülü placeholder "1/1"i kapatıp gerçek ilerlemeyi
            (progress/criteriaValue) çizer. */}
        <div
          className="mx-auto mb-4 relative overflow-hidden rounded-[16px]"
          style={{ width: 260, height: 400, boxShadow: '0px 8px 32px rgba(0,0,0,0.3)' }}
        >
          <img
            src={medal.cardImage}
            alt={medal.title}
            draggable={false}
            className="w-full h-full object-cover"
          />
          <MedalDescriptionOverlay
            description={medal.description}
            tier={medal.tier}
            progress={medal.progress}
            criteriaValue={medal.criteriaValue}
            large
          />
        </div>

        {/* İlerleme — gerçek progress / criteriaValue */}
        {(medal.criteriaValue ?? 0) > 0 && (
          <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#3973bf', marginBottom: 4 }}>
            {medal.progress ?? 0} / {medal.criteriaValue}
          </p>
        )}

        {/* Status — Kazanıldı / Henüz Kazanılmadı (kaynak: owned = earned_at ya da
            aynı ailede daha yüksek tier; global cascade backend'de kaldırıldı) */}
        {medal.owned ? (
          <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 700, color: '#11d600' }}>
            ✓ Kazanıldı
            {medal.earnedAt && (
              <span style={{ fontWeight: 600, color: '#888' }}>
                {' · '}
                {new Date(medal.earnedAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            )}
          </p>
        ) : (
          <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#888' }}>Henüz kazanılmadı</p>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

// Export the static medal catalog for use from other components/pages that
// need the artwork list before the API resolves.
export default StudentProfile;
